import { Suspense, useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

// ---------------------------------------------------------------------------
// Real 3D liquid-chrome arrow — volumetric geometry, PBR black chrome,
// violet neon energy. No 2D image: genuine extruded mesh + liquid droplets.
// ---------------------------------------------------------------------------

const CHROME = {
    color: "#17171d",
    metalness: 1,
    roughness: 0.16,
    envMapIntensity: 2.2,
    clearcoat: 1,
    clearcoatRoughness: 0.08,
};

const VIOLET = new THREE.Color("#8a2be2");

function buildArrowGeometry() {
    // down-arrow silhouette: liquid shaft + V head
    const s = new THREE.Shape();
    s.moveTo(-0.34, 1.35);            // shaft top-left
    s.lineTo(0.34, 1.35);             // shaft top-right
    s.lineTo(0.34, 0.1);              // shaft bottom-right
    s.lineTo(0.95, 0.1);              // wing right tip
    s.lineTo(0, -1.05);               // arrow point
    s.lineTo(-0.95, 0.1);             // wing left tip
    s.lineTo(-0.34, 0.1);             // shaft bottom-left
    s.closePath();
    const geo = new THREE.ExtrudeGeometry(s, {
        depth: 0.34,
        bevelEnabled: true,
        bevelThickness: 0.11,
        bevelSize: 0.09,
        bevelSegments: 6,
        curveSegments: 24,
    });
    geo.center();
    return geo;
}

function Droplet({ position, scale, stretch = 1, material }) {
    const geo = useMemo(() => {
        const g = new THREE.SphereGeometry(0.09, 24, 24);
        // teardrop: stretch up + taper the bottom
        const pos = g.attributes.position;
        for (let i = 0; i < pos.count; i++) {
            const y = pos.getY(i);
            const k = 1 - Math.max(0, -y) * 2.2; // taper bottom into a tail
            pos.setX(i, pos.getX(i) * Math.max(0.25, k));
            pos.setZ(i, pos.getZ(i) * Math.max(0.25, k));
            pos.setY(i, y * stretch + Math.max(0, y) * 0.55); // elongated crown
        }
        g.computeVertexNormals();
        return g;
    }, [stretch]);
    return <mesh geometry={geo} material={material} position={position} scale={scale} />;
}

function SplashCrown({ chrome }) {
    // organic liquid crown above the shaft — twisted ring + droplets
    const swirl = useMemo(() => {
        const g = new THREE.TorusKnotGeometry(0.34, 0.085, 180, 20, 2, 3);
        g.scale(1.15, 0.5, 1.15);
        return g;
    }, []);
    const drops = useMemo(
        () => [
            { p: [-0.62, 1.62, 0.1], s: 0.9, st: 1.2 },
            { p: [0.58, 1.7, -0.05], s: 0.75, st: 1.4 },
            { p: [-0.28, 1.86, 0.12], s: 0.6, st: 1.1 },
            { p: [0.22, 1.95, -0.1], s: 0.5, st: 1.5 },
            { p: [-0.85, 1.38, -0.12], s: 0.55, st: 0.9 },
            { p: [0.9, 1.45, 0.08], s: 0.62, st: 1.0 },
            { p: [0.05, 2.12, 0], s: 0.4, st: 1.3 },
            { p: [-0.45, 1.95, -0.14], s: 0.35, st: 1.2 },
            { p: [0.45, 1.9, 0.14], s: 0.42, st: 1.35 },
        ],
        []
    );
    return (
        <group>
            <mesh geometry={swirl} material={chrome} position={[0, 1.42, 0]} />
            {drops.map((d, i) => (
                <Droplet key={i} position={d.p} scale={d.s} stretch={d.st} material={chrome} />
            ))}
        </group>
    );
}

function NeonCore({}) {
    // embedded violet energy: inner chevron + shaft strip, emissive PBR
    const chevron = useMemo(() => {
        const s = new THREE.Shape();
        s.moveTo(-0.52, 0.02);
        s.lineTo(0, -0.62);
        s.lineTo(0.52, 0.02);
        s.lineTo(0.34, 0.2);
        s.lineTo(0, -0.28);
        s.lineTo(-0.34, 0.2);
        s.closePath();
        const g = new THREE.ExtrudeGeometry(s, { depth: 0.05, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.02, bevelSegments: 3 });
        g.center();
        return g;
    }, []);
    const strip = useMemo(() => {
        const g = new THREE.CapsuleGeometry(0.055, 0.85, 8, 16);
        return g;
    }, []);
    const mat = useMemo(
        () =>
            new THREE.MeshPhysicalMaterial({
                color: "#2a0a4a",
                emissive: VIOLET,
                emissiveIntensity: 2.4,
                metalness: 0.4,
                roughness: 0.25,
            }),
        []
    );
    return (
        <group>
            <mesh geometry={chevron} material={mat} position={[0, -0.34, 0.26]} />
            <mesh geometry={strip} material={mat} position={[0, 0.72, 0.26]} />
        </group>
    );
}

function ArrowModel() {
    const group = useRef(null);
    const chrome = useMemo(() => new THREE.MeshPhysicalMaterial(CHROME), []);
    const arrowGeo = useMemo(buildArrowGeometry, []);
    const mouse = useRef({ x: 0, y: 0, lx: 0, ly: 0 });

    useEffect(() => {
        const onMove = (e) => {
            mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
            mouse.current.y = (e.clientY / window.innerHeight) * 2 - 1;
        };
        window.addEventListener("mousemove", onMove);
        return () => window.removeEventListener("mousemove", onMove);
    }, []);

    useFrame((state) => {
        const g = group.current;
        if (!g) return;
        const t = state.clock.elapsedTime;
        const m = mouse.current;
        m.lx += (m.x - m.lx) * 0.04;
        m.ly += (m.y - m.ly) * 0.04;
        // continuous 360° spin + organic tilt
        g.rotation.y = t * 0.55 + m.lx * 0.35;
        g.rotation.x = Math.sin(t * 0.42 + 0.5) * 0.12 - m.ly * 0.18;
        g.rotation.z = Math.sin(t * 0.31 + 1.2) * 0.07 + m.lx * 0.08;
        // zero-gravity float (smaller & lower on phones so the section text stays clear)
        const mobile = window.innerWidth < 768;
        g.position.y = Math.sin(t * 0.6) * 0.22 + Math.sin(t * 0.23 + 1.7) * 0.1 - (mobile ? 0.55 : 0);
        g.position.x = Math.sin(t * 0.34 + 0.8) * 0.12;
        // scroll-synced shrink (fade handled on the canvas wrapper)
        const target = window.innerHeight * 1.15;
        const p = Math.min(1, Math.max(0, window.scrollY / target));
        const shrink = 1 - Math.pow(p, 1.15);
        const intro = Math.min(1, Math.max(0, (t - 0.2) / 1.4));
        const introEase = 1 - Math.pow(1 - intro, 3);
        g.scale.setScalar(Math.max(0.001, (mobile ? 0.5 : 0.82) * introEase * shrink));
        g.visible = shrink > 0.01;
    });

    return (
        <group ref={group} position={[0, 0.1, 0]}>
            <mesh geometry={arrowGeo} material={chrome} castShadow receiveShadow />
            <SplashCrown chrome={chrome} />
            <NeonCore />
        </group>
    );
}

function SceneLights() {
    const { gl, scene } = useThree();
    useEffect(() => {
        const pmrem = new THREE.PMREMGenerator(gl);
        const env = pmrem.fromScene(new RoomEnvironment(), 0.06).texture;
        scene.environment = env;
        return () => {
            scene.environment = null;
            env.dispose();
            pmrem.dispose();
        };
    }, [gl, scene]);
    return (
        <>
            <ambientLight intensity={0.25} />
            <directionalLight position={[4, 6, 5]} intensity={1.4} color="#ffffff" />
            <pointLight position={[-5, 2, 3]} intensity={26} color="#8a2be2" />
            <pointLight position={[5, -3, 2]} intensity={14} color="#d4af37" />
            <pointLight position={[0, 4, -4]} intensity={18} color="#5b1fb0" />
        </>
    );
}

export default function FloatingArrow3D({ visible = true }) {
    const wrap = useRef(null);
    useEffect(() => {
        let raf;
        const start = performance.now();
        const loop = (now) => {
            const el = wrap.current;
            if (el) {
                const t = (now - start) / 1000;
                const intro = Math.min(1, Math.max(0, (t - 2.4) / 1.2)); // after the curtain
                const target = window.innerHeight * 1.15;
                const p = Math.min(1, Math.max(0, window.scrollY / target));
                const opacity = (1 - Math.pow(p, 1.6)) * (1 - Math.pow(1 - intro, 3));
                el.style.opacity = opacity.toFixed(3);
                el.style.visibility = opacity < 0.02 ? "hidden" : "visible";
            }
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, []);

    // click → the arrow dives down and the page flows into the Gloom Experience tunnel
    const dive = () => {
        const y = window.innerHeight * 1.35;
        if (window.__lenis) window.__lenis.scrollTo(y, { duration: 1.6 });
        else window.scrollTo({ top: y, behavior: "smooth" });
    };

    return (
        <div
            ref={wrap}
            data-testid="arrow-3d-canvas"
            className="pointer-events-none fixed inset-0 z-[55]"
            style={{ opacity: 0, display: visible ? "block" : "none" }}
        >
            <Canvas
                gl={{ alpha: true, antialias: true }}
                dpr={[1, 2]}
                camera={{ position: [0, 0.4, 6.2], fov: 42 }}
                style={{ background: "transparent" }}
            >
                <Suspense fallback={null}>
                    <SceneLights />
                    <ArrowModel />
                </Suspense>
            </Canvas>
            {/* invisible hit zone over the arrow only */}
            <button
                data-testid="arrow-3d-hit"
                data-cursor="DIVE"
                onClick={dive}
                aria-label="Descendre dans l'expérience Gloom"
                className="pointer-events-auto absolute left-1/2 top-1/2 h-[52vh] w-[40vh] -translate-x-1/2 -translate-y-1/2 cursor-pointer"
            />
        </div>
    );
}
